// Sample Ads library for Anne Flinn Advertising.
//
// Two libraries, two layouts:
//   1. `videoAds`        → one card per video (TV / Social commercials + Reels)
//   2. `displayCampaigns` → one card PER CLIENT campaign, with fixed size slots.
//      Adding a new client means appending one campaign with that client's
//      ads dropped into the matching size slots — no new card layouts.
//
// Standard IAB display slots used per campaign:
//   - mrec          → 300×250 Medium Rectangle   (aspect 6/5)
//   - interstitial  → 320×480 Mobile Interstitial (aspect 2/3)
//   - banners       → 320×50  Mobile Banners      (stacked vertically, aspect 6.4/1)
//
// Each campaign supports any subset of slots — missing slots are simply
// not rendered. So a client with only an MREC + banner still works.

// ─────────────────────────────────────────────────────────────
// VIDEO ADS — TV commercials, social spots, vertical Reels
// ─────────────────────────────────────────────────────────────
export const videoAds = [
  // ───────────────────────────────────────────────────────────
  // Sticks Kebob Shop · Vertical Reels (3 spots)
  // 9:16 paid-social reels: January winter promo, Summer Fresh.
  // Fast. Healthy., and Football tailgate-season cut.
  // ───────────────────────────────────────────────────────────
  {
    id: "sticks-jan-vertical-reel",
    client: "Sticks Kebob Shop · January Reel",
    industry: "Restaurant / Food & Beverage",
    tradeSlug: "restaurants",
    vertical: "tourism",
    format: "reel",
    status: "aired",
    src: "/media/ads/video/sticks-jan-vertical.mp4",
    poster: "/media/ads/video/sticks-jan-vertical.jpg",
    handle: "@stickskebob",
    caption:
      "January post-holiday-reset reel — fresh, healthy Mediterranean for the new-year crowd. Vertical 9:16, built for Reels and Stories placements.",
    aspect: "9/16",
  },
  {
    id: "sticks-summer-fresh-reel",
    client: "Sticks Kebob Shop · Summer Fresh. Fast. Healthy. Reel",
    industry: "Restaurant / Food & Beverage",
    tradeSlug: "restaurants",
    vertical: "tourism",
    format: "reel",
    status: "aired",
    src: "/media/ads/video/sticks-summer-fresh.mp4",
    poster: "/media/ads/video/sticks-summer-fresh.jpg",
    handle: "@stickskebob",
    caption:
      "Summer reel hitting the Sticks brand pillars — Fresh. Fast. Healthy. Vertical lifestyle cuts of bowls, wraps, and outdoor patio energy.",
    aspect: "9/16",
  },
  {
    id: "sticks-football-reel",
    client: "Sticks Kebob Shop · Football Tailgate Reel",
    industry: "Restaurant / Food & Beverage",
    tradeSlug: "restaurants",
    vertical: "tourism",
    format: "reel",
    status: "aired",
    src: "/media/ads/video/sticks-football.mp4",
    poster: "/media/ads/video/sticks-football.jpg",
    handle: "@stickskebob",
    caption:
      "Football-season reel — catering trays, family-style platters, and game-day energy. Vertical paid-social spot timed to Saturday and Sunday viewing windows.",
    aspect: "9/16",
  },
  // ───────────────────────────────────────────────────────────
  // Sticks Kebob Shop · Menu Carousel · Facebook (5 slides)
  // Restaurant menu showcase — wraps, salads, platters, sides,
  // seasonal bowls. Pure food photography + protein/side callouts.
  // ───────────────────────────────────────────────────────────
  {
    id: "sticks-menu-carousel",
    client: "Sticks Kebob Shop · Menu Carousel",
    industry: "Restaurant / Food & Beverage",
    tradeSlug: "restaurants",
    vertical: "tourism",
    format: "social-carousel",
    status: "aired",
    carousel: {
      page: {
        name: "Sticks Kebob Shop",
      },
      displayUrl: "stickskebobshop.com",
      body:
        "Mediterranean fast-casual, done right. 🥙 Choice of proteins, fresh-made sides, and bowls that change with the seasons. Wraps · Salads · Platters · Sides · Seasonal Bowls — what are you ordering today?",
      slides: [
        {
          src: "/media/ads/social/sticks-menu-1.webp",
          headline: "Wraps · Chicken, Beef, Lamb, Shrimp, Falafel",
        },
        {
          src: "/media/ads/social/sticks-menu-2.webp",
          headline: "Salads · Add shrimp, chicken, beef, or falafel",
        },
        {
          src: "/media/ads/social/sticks-menu-3.webp",
          headline: "Platters · Choice of protein, sauce & sides",
        },
        {
          src: "/media/ads/social/sticks-menu-4.webp",
          headline: "Sides · Hummus, tabouleh, grilled veggies & more",
        },
        {
          src: "/media/ads/social/sticks-menu-5.webp",
          headline: "Seasonal Bowls · Check the menu",
        },
      ],
    },
    caption:
      "5-card Facebook carousel ad for Sticks Kebob Shop showcasing the full menu architecture — wraps, salads, platters, sides, and seasonal bowls. Each card pairs lifestyle food photography with the protein/side callouts that drive add-on order value.",
    aspect: "1/1",
  },
  // ───────────────────────────────────────────────────────────
  // Pace Academy · Video Slideshow · TV/Video commercial
  // School slideshow built from campus photography.
  // ───────────────────────────────────────────────────────────
  {
    id: "pace-academy-video-slideshow",
    client: "Pace Academy · Video Slideshow",
    industry: "Children / Schools / Camps",
    tradeSlug: "events-festivals",
    vertical: "tourism",
    format: "tv-social",
    status: "aired",
    src: "/media/ads/video/pace-academy-video-slideshow.mp4",
    poster: "/media/ads/video/pace-academy-video-slideshow.jpg",
    caption:
      "Pace Academy slideshow spot built from campus photography — students, classrooms, athletics, and traditions cut to a single recruitment narrative.",
    aspect: "16/9",
  },
  // ───────────────────────────────────────────────────────────
  // Pace Academy · Open House 15s
  // Short 15-second cut of the same campaign for paid social.
  // ───────────────────────────────────────────────────────────
  {
    id: "pace-academy-open-house-15",
    client: "Pace Academy · Open House (15-second cut)",
    industry: "Children / Schools / Camps",
    tradeSlug: "events-festivals",
    vertical: "tourism",
    format: "tv-social",
    status: "aired",
    src: "/media/ads/video/pace-academy-open-house-15.mp4",
    poster: "/media/ads/video/pace-academy-open-house-15.jpg",
    caption:
      "15-second cut of Pace Academy's Open House campaign — designed for paid social and pre-roll inventory where attention spans demand tight pacing and a clear CTA.",
    aspect: "16/9",
  },
  // ───────────────────────────────────────────────────────────
  // Young Chefs Academy · Summer Camps Charleston · TV/Video
  // ───────────────────────────────────────────────────────────
  {
    id: "yca-summer-camps-charleston",
    client: "Young Chefs Academy · Summer Camps (Charleston)",
    industry: "Children / Schools / Camps",
    tradeSlug: "events-festivals",
    vertical: "tourism",
    format: "tv-social",
    status: "aired",
    src: "/media/ads/video/yca-summer-camps-charleston.mp4",
    poster: "/media/ads/video/yca-summer-camps-charleston.jpg",
    caption:
      "Young Chefs Academy Charleston summer camp promo — kids cooking real food, parents booking real weeks. Built for paid social to drive seasonal registrations.",
    aspect: "16/9",
  },
  // ───────────────────────────────────────────────────────────
  // Young Chefs Academy · July Camps · TV/Video
  // ───────────────────────────────────────────────────────────
  {
    id: "yca-july-camps",
    client: "Young Chefs Academy · July Camps",
    industry: "Children / Schools / Camps",
    tradeSlug: "events-festivals",
    vertical: "tourism",
    format: "tv-social",
    status: "aired",
    src: "/media/ads/video/yca-july-camps.mp4",
    poster: "/media/ads/video/yca-july-camps.jpg",
    caption:
      "Young Chefs Academy July camp paid-social spot — quick-cut energy and kitchen action to convert last-minute summer signups.",
    aspect: "16/9",
  },
  // ───────────────────────────────────────────────────────────
  // Run the Park: Park2Park · Event video commercial
  // 21-second event spot for the Park2Park run.
  // ───────────────────────────────────────────────────────────
  {
    id: "run-the-park-park2park",
    client: "Run the Park · Park2Park",
    industry: "Event",
    tradeSlug: "events-festivals",
    vertical: "tourism",
    format: "tv-social",
    status: "aired",
    src: "/media/ads/video/run-the-park-park2park.mp4",
    poster: "/media/ads/video/run-the-park-park2park.jpg",
    caption:
      "21-second TV / paid-social spot for Run the Park's Park2Park event. Built to drive bib registrations across the Charlottesville and Shenandoah Valley running community.",
    aspect: "16/9",
  },
  // ───────────────────────────────────────────────────────────
  // Cunningham Creek Winery · Run Thru the Vines 5K · Facebook CAROUSEL
  // 4-card carousel promoting the 5K Wine Run at the winery.
  // ───────────────────────────────────────────────────────────
  {
    id: "cuncreek-run-thru-the-vines-carousel",
    client: "Cunningham Creek Winery · Run Thru the Vines 5K Carousel",
    industry: "Winery / Brewery / Cidery / Venue",
    tradeSlug: "wineries-breweries",
    vertical: "tourism",
    format: "social-carousel",
    status: "aired",
    carousel: {
      page: {
        name: "Cunningham Creek Winery",
      },
      displayUrl: "cunninghamcreekwinery.com",
      body:
        "🏃‍♀️🍷 Lace up, run the vineyard, sip the reward. The 5K Wine Run returns to Cunningham Creek Winery — Run Now, Wine Later. Bring the family, bring the dog, bring your running shoes. Registration open.",
      slides: [
        {
          src: "/media/ads/social/cuncreek-rttv-1.webp",
          headline: "5K Wine Run — Run Now, Wine Later",
        },
        {
          src: "/media/ads/social/cuncreek-rttv-2.webp",
          headline: "Kids, families, and first-timers welcome",
        },
        {
          src: "/media/ads/social/cuncreek-rttv-3.webp",
          headline: "Cross the finish line all smiles",
        },
        {
          src: "/media/ads/social/cuncreek-rttv-4.webp",
          headline: "Dog-friendly post-race party in the vines",
        },
      ],
    },
    caption:
      "4-card Facebook carousel ad for Cunningham Creek Winery's signature 5K Wine Run event series — \"Run Thru the Vines.\" Lifestyle photography pairs the runners with the vineyard setting and finish-line medals to drive registration sign-ups.",
    aspect: "1/1",
  },
  // ───────────────────────────────────────────────────────────
  // WNRN · Facebook CAROUSEL ad (5 slides)
  // Independent music radio station promo: App, FM frequencies,
  // Membership drive, Smart speaker, Worldwide stream.
  // ───────────────────────────────────────────────────────────
  {
    id: "wnrn-radio-carousel",
    client: "WNRN · For Music People Carousel",
    industry: "Non-Profit",
    tradeSlug: "events-festivals",
    vertical: "tourism",
    format: "social-carousel",
    status: "aired",
    carousel: {
      page: {
        name: "WNRN Independent Music Radio",
      },
      displayUrl: "wnrn.org",
      body:
        "Listener-supported, independent music radio — broadcasting from the Blue Ridge to the Bay. 🎶 Find us on the dial, in the app, on your smart speaker, or streaming worldwide. For Music People.",
      slides: [
        {
          src: "/media/ads/social/wnrn-carousel-1.webp",
          headline: "Download the free WNRN App — iPhone + Android",
        },
        {
          src: "/media/ads/social/wnrn-carousel-2.webp",
          headline: "From the Blue Ridge to the Bay — find your local FM",
        },
        {
          src: "/media/ads/social/wnrn-carousel-3.webp",
          headline: "Support independent music radio · Become a member",
        },
        {
          src: "/media/ads/social/wnrn-carousel-4.webp",
          headline: "Tell your smart speaker: \"Play WNRN\"",
        },
        {
          src: "/media/ads/social/wnrn-carousel-5.webp",
          headline: "Stream WNRN online worldwide at WNRN.org",
        },
      ],
    },
    caption:
      "5-card Facebook carousel ad for WNRN, the listener-supported independent music station. Color-blocked slides walk listeners through every way to tune in — FM, app, smart speaker, online stream — and close with a membership ask. Same Meta carousel chrome we run for tourism, dining, and retail brands.",
    aspect: "1/1",
  },
  // ───────────────────────────────────────────────────────────
  // Stinson Vineyards · Facebook CAROUSEL ad (5 slides)
  // Tannat / Meritage / vineyard lifestyle campaign for the
  // Monticello AVA. Same Meta carousel chrome we use everywhere.
  // ───────────────────────────────────────────────────────────
  {
    id: "stinson-vineyards-carousel",
    client: "Stinson Vineyards · Tannat & Vineyard Carousel",
    industry: "Winery / Brewery / Cidery / Venue",
    tradeSlug: "wineries-breweries",
    vertical: "tourism",
    format: "social-carousel",
    status: "aired",
    carousel: {
      page: {
        name: "Stinson Vineyards",
      },
      displayUrl: "stinsonvineyards.com",
      body:
        "Estate-grown, Monticello AVA, family-run since 2009. 🍷 From the Meritage in the cellar to a glass of Tannat by the grill — this is what the Blue Ridge tastes like. Plan your visit.",
      slides: [
        {
          src: "/media/ads/social/stinson-carousel-1.webp",
          headline: "Aerial of the estate — vines, pool, and the farmhouse",
        },
        {
          src: "/media/ads/social/stinson-carousel-2.webp",
          headline: "Hand-packing the 2016 Meritage in the cellar",
        },
        {
          src: "/media/ads/social/stinson-carousel-3.webp",
          headline: "Taste the lineup — Tannat, Rosé, and Estate White",
        },
        {
          src: "/media/ads/social/stinson-carousel-4.webp",
          headline: "Monticello AVA vineyard with the Blue Ridge beyond",
        },
        {
          src: "/media/ads/social/stinson-carousel-5.webp",
          headline: "Pair the Tannat with whatever's on the Big Green Egg",
        },
      ],
    },
    caption:
      "5-card Facebook carousel ad — Stinson Vineyards Tannat + estate-lifestyle campaign. Same Meta carousel chrome we run for tourism, events, and culinary brands. Drove visit-the-winery conversions across the Monticello Wine Trail.",
    aspect: "1/1",
  },
  // ───────────────────────────────────────────────────────────
  // Blue Ridge Mountain Maze · Corn Maze · Facebook CAROUSEL ad
  // 4-card horizontal carousel showcasing the maze across
  // sunset/daytime/pathway/evening atmospheres.
  // ───────────────────────────────────────────────────────────
  {
    id: "brmm-corn-maze-carousel",
    client: "Blue Ridge Mountain Maze · Corn Maze Carousel",
    industry: "",
    tradeSlug: "events-festivals",
    vertical: "tourism",
    format: "social-carousel",
    status: "aired",
    carousel: {
      page: {
        name: "Blue Ridge Mountain Maze",
      },
      displayUrl: "blueridgemountainmaze.com",
      body:
        "Get lost (and found) in the corn this fall. 🌽 Sunsets over the Blue Ridge, pumpkin patches, evening festival vibes — the corn maze is open. Bring the whole crew.",
      slides: [
        {
          src: "/media/ads/social/brmm-corn-maze-1.webp",
          headline: "Pumpkin patch + sunset views over the Blue Ridge",
        },
        {
          src: "/media/ads/social/brmm-corn-maze-2.webp",
          headline: "Bring the family — kids love the maze map hunt",
        },
        {
          src: "/media/ads/social/brmm-corn-maze-3.webp",
          headline: "Wind through the corn at golden hour",
        },
        {
          src: "/media/ads/social/brmm-corn-maze-4.webp",
          headline: "Stay for the evening festival",
        },
      ],
    },
    caption:
      "4-card Facebook carousel ad — Blue Ridge Mountain Maze fall season. Same Meta carousel chrome we sell to every tourism brand: snap-scroll cards, single CTA per card, consistent brand badge across slides.",
    aspect: "1/1",
  },
  // ───────────────────────────────────────────────────────────
  // Young Chefs Academy · Gift Membership · Facebook CAROUSEL ad
  // Five-card horizontal carousel that ran in the Meta news feed.
  // Rendered using the real Meta carousel-ad chrome (page header,
  // body copy, snap-scroll cards, dot indicators, reactions row).
  // ───────────────────────────────────────────────────────────
  {
    id: "yca-gift-membership-carousel",
    client: "Young Chefs Academy · Gift Membership Carousel",
    industry: "Children / Schools / Camps",
    tradeSlug: "events-festivals",
    vertical: "tourism",
    format: "social-carousel",
    status: "aired",
    carousel: {
      page: {
        name: "Young Chefs Academy",
      },
      displayUrl: "youngchefsacademy.com",
      body:
        "Give the gift of a skill that lasts a lifetime this holiday season. Membership bundles, exclusive chef coats, and unforgettable kitchen memories — all in one place. 👩‍🍳",
      slides: [
        {
          src: "/media/ads/social/yca-carousel-1.webp",
          headline: "Give the gift that will enrich a child's life",
        },
        {
          src: "/media/ads/social/yca-carousel-2.webp",
          headline: "All members receive an exclusive chef coat",
        },
        {
          src: "/media/ads/social/yca-carousel-3.webp",
          headline: "Membership bundles of 2, 4 & 6 months available",
        },
        {
          src: "/media/ads/social/yca-carousel-4.webp",
          headline: "Give the gift of membership to Young Chefs Academy",
        },
        {
          src: "/media/ads/social/yca-carousel-5.webp",
          headline: "Gift cooking skills that will last a lifetime",
        },
      ],
    },
    caption:
      "5-card Facebook carousel ad — gift-membership holiday campaign. Snap-scroll cards, brand-consistent border treatment across every slide, single Meta CTA per card. Built to drive seasonal sign-ups.",
    aspect: "1/1",
  },
  // ───────────────────────────────────────────────────────────
  // Young Chefs Academy · Facebook / Instagram static ads
  // Square + 1.91:1 creatives, ran in paid social newsfeeds.
  // ───────────────────────────────────────────────────────────
  {
    id: "yca-holiday-cookie",
    client: "Young Chefs Academy · Holiday Cookie Workshop",
    industry: "Children / Schools / Camps",
    tradeSlug: "events-festivals",
    vertical: "tourism",
    format: "social-static",
    status: "aired",
    src: "/media/ads/social/yca-cookie.webp",
    caption:
      "Holiday-season Facebook + Instagram newsfeed ad driving cookie-decorating workshop sign-ups.",
    aspect: "5/4",
  },
  {
    id: "yca-buche-de-noel",
    client: "Young Chefs Academy · Buche de Noël Workshop",
    industry: "Children / Schools / Camps",
    tradeSlug: "events-festivals",
    vertical: "tourism",
    format: "social-static",
    status: "aired",
    src: "/media/ads/social/yca-buche.webp",
    caption:
      "Holiday showstopper workshop creative for Facebook + Instagram, paired Buche de Noël photography with bold reserve-now CTA.",
    aspect: "1.91/1",
  },
  {
    id: "yca-ready-set-bake",
    client: "Young Chefs Academy · Ready. Set. Bake. Pastry Workshop",
    industry: "Children / Schools / Camps",
    tradeSlug: "events-festivals",
    vertical: "tourism",
    format: "social-static",
    status: "aired",
    src: "/media/ads/social/yca-pastry.webp",
    caption:
      "Pastry-themed paid social ad for a one-night workshop. High-energy headline treatment built for newsfeed scroll-stop.",
    aspect: "1.91/1",
  },
  {
    id: "yca-bewitching-bites",
    client: "Young Chefs Academy · Bewitching Bites Halloween Workshop",
    industry: "Children / Schools / Camps",
    tradeSlug: "events-festivals",
    vertical: "tourism",
    format: "social-static",
    status: "aired",
    src: "/media/ads/social/yca-halloween.webp",
    caption:
      "Halloween-themed Charleston, SC workshop ad. Two-time-slot CTA layout designed for Meta reservation conversions.",
    aspect: "5/4",
  },
  {
    id: "yca-family-in-the-kitchen",
    client: "Young Chefs Academy · Family in the Kitchen",
    industry: "Children / Schools / Camps",
    tradeSlug: "events-festivals",
    vertical: "tourism",
    format: "social-static",
    status: "aired",
    src: "/media/ads/social/yca-family.webp",
    caption:
      "Mediterranean family-cooking workshop ad for Richmond, VA. Lifestyle photography + clear date/time stamp.",
    aspect: "5/4",
  },
  {
    id: "mack-morris-tv",
    client: "Mack Morris Heating & Air",
    industry: "HVAC",
    tradeSlug: "hvac",
    vertical: "home-services",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/mack-morris-horizontal.mp4",
    poster: "/media/reel/mack-morris-horizontal.jpg",
    caption:
      "TV + social commercial built for a regional HVAC pro. Localized voice, fast brand recall, and a clear emergency-service hook.",
    aspect: "16/9",
  },
  {
    id: "mack-morris-reel",
    client: "Mack Morris Heating & Air",
    industry: "HVAC",
    tradeSlug: "hvac",
    vertical: "home-services",
    format: "reel",
    status: "aired",
    src: "/media/reel/mack-morris-vertical.mp4",
    poster: "/media/reel/mack-morris-vertical.jpg",
    caption:
      "Vertical Reels cut of the same campaign, built for Instagram, TikTok, and YouTube Shorts placement.",
    aspect: "9/16",
  },
  {
    id: "rts-roofing-tv",
    client: "RTS Roofing",
    industry: "Roofing",
    tradeSlug: "roofers",
    vertical: "home-services",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/rts-roofing.mp4",
    poster: "/media/reel/rts-roofing.jpg",
    caption:
      "TV + social commercial for a residential roofing contractor, storm-season hook, local trust signals, and a strong call-to-call.",
    aspect: "16/9",
  },
  {
    id: "rts-roofing-social",
    client: "RTS Roofing",
    industry: "Roofing",
    tradeSlug: "roofers",
    vertical: "home-services",
    format: "reel",
    status: "aired",
    src: "/media/reel/rts-roofing-social.mp4",
    poster: "/media/reel/rts-roofing-social.jpg",
    caption:
      "Paid-social spot for RTS Roofing, sized for Instagram Feed (4:5). Build brand recognition for the roofing contractor homeowners remember when storms hit.",
    aspect: "4/5",
  },
  {
    id: "luxury-ireland-website",
    client: "Luxury Travel Company · Ireland",
    industry: "Luxury Travel",
    tradeSlug: null,
    vertical: "tourism",
    format: "reel",
    status: "aired",
    src: "/media/reel/luxury-ireland-website.mp4",
    poster: "/media/reel/luxury-ireland-website.jpg",
    caption:
      "Hero reel cut for the luxury travel company's website and Meta paid placements, lifestyle-led Ireland visuals that sell the daydream of the trip.",
    aspect: "9/16",
  },
  {
    id: "luxury-ireland-set-a",
    client: "Luxury Travel Company · Ireland",
    industry: "Luxury Travel",
    tradeSlug: null,
    vertical: "tourism",
    format: "reel",
    status: "aired",
    src: "/media/reel/luxury-ireland-a.mp4",
    poster: "/media/reel/luxury-ireland-a.jpg",
    caption:
      "Luxury Ireland Set A, vertical paid-social spot for Instagram Reels + TikTok placement. Cinematic landscape b-roll opens to a trip-package CTA.",
    aspect: "9/16",
  },
  {
    id: "luxury-ireland-set-b",
    client: "Luxury Travel Company · Ireland",
    industry: "Luxury Travel",
    tradeSlug: null,
    vertical: "tourism",
    format: "reel",
    status: "aired",
    src: "/media/reel/luxury-ireland-b.mp4",
    poster: "/media/reel/luxury-ireland-b.jpg",
    caption:
      "Luxury Ireland Set B, vertical paid-social cut emphasizing castle stays and private touring. Built for high-intent travel-planner audiences.",
    aspect: "9/16",
  },
  {
    id: "luxury-ireland-set-c",
    client: "Luxury Travel Company · Ireland",
    industry: "Luxury Travel",
    tradeSlug: null,
    vertical: "tourism",
    format: "reel",
    status: "aired",
    src: "/media/reel/luxury-ireland-c.mp4",
    poster: "/media/reel/luxury-ireland-c.jpg",
    caption:
      "Luxury Ireland Set C, vertical paid-social spot leaning into coastal scenes and gourmet pairings. Aimed at experiential luxury travelers.",
    aspect: "9/16",
  },
  {
    id: "janice-blenheim-reel",
    client: "Janice · Blenheim Area Home Tour",
    industry: "Real Estate / Developer / Home Builder",
    tradeSlug: "real-estate",
    vertical: "home-services",
    format: "reel",
    status: "aired",
    src: "/media/reel/janice-blenheim.mp4",
    poster: "/media/reel/janice-blenheim.jpg",
    handle: "@janicerealtor",
    caption:
      "Realtor 'minute' walkthrough of a Blenheim-area listing. Vertical Instagram Reels cut driving showings and saved-property follows.",
    aspect: "9/16",
  },
  {
    id: "bh-reel",
    client: "Belmont Homes · Listing Reel",
    industry: "Real Estate / Developer / Home Builder",
    tradeSlug: "real-estate",
    vertical: "home-services",
    format: "reel",
    status: "aired",
    src: "/media/reel/bh-reel.mp4",
    poster: "/media/reel/bh-reel.jpg",
    handle: "@belmonthomes",
    caption:
      "Vertical Reels cut for a residential real-estate listing. Quick-cut walkthrough designed to stop scrollers on Instagram and TikTok.",
    aspect: "9/16",
  },
  {
    id: "dave-downtown-mall-reel",
    client: "Dave · Downtown Mall",
    industry: "Real Estate / Developer / Home Builder",
    tradeSlug: "real-estate",
    vertical: "home-services",
    format: "reel",
    status: "aired",
    src: "/media/reel/dave-downtown.mp4",
    poster: "/media/reel/dave-downtown.jpg",
    handle: "@anneflinn",
    caption:
      "Walking-tour Reel of the Charlottesville Downtown Mall. Tourism-forward creative driving visitor footfall and local-discovery saves.",
    aspect: "9/16",
  },
  {
    id: "nelson-tiktok-mark-d",
    client: "Visit Nelson County · Mark D Reel",
    industry: "Tourism DMO",
    tradeSlug: "dmos",
    vertical: "tourism",
    format: "reel",
    status: "aired",
    src: "/media/reel/nelson-tiktok-markd.mp4",
    poster: "/media/reel/nelson-tiktok-markd.jpg",
    handle: "@visitnelsoncounty",
    caption:
      "Influencer-style Reel for Nelson County tourism, TikTok-native cut driving destination saves and trip-planning intent.",
    aspect: "9/16",
  },
  {
    id: "nelson-151-winter",
    client: "Nelson 151 · Winter Trail",
    industry: "Tourism DMO",
    tradeSlug: "dmos",
    vertical: "tourism",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/nelson151-winter.mp4",
    poster: "/media/reel/nelson151-winter.jpg",
    caption:
      "TV + social commercial for the Nelson 151 winery & brewery trail, winter campaign driving tasting-room visits across the corridor.",
    aspect: "16/9",
  },
  {
    id: "wintergreen-hiking",
    client: "Wintergreen Resort · Hiking",
    industry: "Tourism DMO",
    tradeSlug: "attractions",
    vertical: "tourism",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/wintergreen-hiking.mp4",
    poster: "/media/reel/wintergreen-hiking.jpg",
    caption:
      "TV + social commercial for Wintergreen Resort, hiking-season creative driving overnight bookings and day-pass visits.",
    aspect: "16/9",
  },
  {
    id: "stinson-vineyards",
    client: "Stinson Vineyards",
    industry: "Winery / Brewery / Cidery / Venue",
    tradeSlug: "wineries-breweries",
    vertical: "tourism",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/stinson.mp4",
    poster: "/media/reel/stinson.jpg",
    caption:
      "TV + social commercial for a Central Virginia estate winery. Lifestyle-led cinematography driving tasting-room visits and wine-club sign-ups.",
    aspect: "16/9",
  },
  {
    id: "cunningham-creek-slideshow",
    client: "Cunningham Creek Winery · Slideshow",
    industry: "Winery / Brewery / Cidery / Venue",
    tradeSlug: "wineries-breweries",
    vertical: "tourism",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/ccw-slideshow.mp4",
    poster: "/media/reel/ccw-slideshow.jpg",
    caption:
      "TV + social slideshow commercial for Cunningham Creek Winery at Middle Fork Farm. Estate-to-glass cinematic montage driving tasting-room visits and event-rental inquiries.",
    aspect: "16/9",
  },
  {
    id: "ccw-summer-2022",
    client: "Cunningham Creek Winery · Summer",
    industry: "Winery / Brewery / Cidery / Venue",
    tradeSlug: "wineries-breweries",
    vertical: "tourism",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/ccw-summer.mp4",
    poster: "/media/reel/ccw-summer.jpg",
    caption:
      "TV + social summer commercial for Cunningham Creek Winery, lifestyle-led creative driving warm-weather visits, weddings, and event-rental inquiries.",
    aspect: "16/9",
  },
  {
    id: "ccw-january",
    client: "Cunningham Creek Winery · January",
    industry: "Winery / Brewery / Cidery / Venue",
    tradeSlug: "wineries-breweries",
    vertical: "tourism",
    format: "reel",
    status: "aired",
    src: "/media/reel/ccw-january.mp4",
    poster: "/media/reel/ccw-january.jpg",
    handle: "@cunninghamcreekwinery",
    caption:
      ":19 vertical social cut for Cunningham Creek Winery, January seasonal creative driving off-peak tasting-room visits and wine-club sign-ups.",
    aspect: "9/16",
  },
  {
    id: "ccw-more-joy",
    client: "The Club at Glenmore · More Joy",
    industry: "Winery / Brewery / Cidery / Venue",
    tradeSlug: "wineries-breweries",
    vertical: "tourism",
    format: "reel",
    status: "aired",
    src: "/media/reel/ccw-more-joy.mp4",
    poster: "/media/reel/ccw-more-joy.jpg",
    handle: "@theclubatglenmore",
    caption:
      ":14 vertical Reel for The Club at Glenmore, \"More Joy\" holiday creative spotlighting the estate and wedding-venue experience.",
    aspect: "9/16",
  },
  {
    id: "glenmore-wedding-venue",
    client: "The Club at Glenmore · Wedding Venue",
    industry: "Winery / Brewery / Cidery / Venue",
    tradeSlug: "wineries-breweries",
    vertical: "tourism",
    format: "reel",
    status: "aired",
    src: "/media/reel/glenmore-wedding.mp4",
    poster: "/media/reel/glenmore-wedding.jpg",
    handle: "@theclubatglenmore",
    caption:
      ":14 vertical Reel for The Club at Glenmore, premium wedding-venue creative spotlighting the estate and event spaces.",
    aspect: "9/16",
  },
  {
    id: "bella-terra-landscapes",
    client: "Bella Terra Landscapes",
    industry: "Landscaping / Lawn & Garden",
    tradeSlug: "landscaping",
    vertical: "home-services",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/bellaterra-landscapes.mp4",
    poster: "/media/reel/bellaterra-landscapes.jpg",
    caption:
      ":27 TV + social commercial for Bella Terra Landscapes, premium landscape-design creative driving consultation requests and project inquiries.",
    aspect: "16/9",
  },
  {
    id: "loving-cup-branding",
    client: "Loving Cup Vineyard · Branding",
    industry: "Winery / Brewery / Cidery / Venue",
    tradeSlug: "wineries-breweries",
    vertical: "tourism",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/lovingcup-branding.mp4",
    poster: "/media/reel/lovingcup-branding.jpg",
    caption:
      "TV + social brand commercial for Loving Cup Vineyard, Virginia's certified-organic winery. Story-led creative driving tasting-room visits and wine-club sign-ups.",
    aspect: "16/9",
  },
  {
    id: "loving-cup-kendall-vo",
    client: "Loving Cup Vineyard · Kendall VO",
    industry: "Winery / Brewery / Cidery / Venue",
    tradeSlug: "wineries-breweries",
    vertical: "tourism",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/lovingcup-kendall.mp4",
    poster: "/media/reel/lovingcup-kendall.jpg",
    caption:
      "TV + social commercial for Loving Cup Vineyard with on-camera voiceover, story-driven creative spotlighting the certified-organic estate experience.",
    aspect: "16/9",
  },
  {
    id: "loving-cup-story-gift",
    client: "Loving Cup Vineyard · Story",
    industry: "Winery / Brewery / Cidery / Venue",
    tradeSlug: "wineries-breweries",
    vertical: "tourism",
    format: "reel",
    status: "aired",
    src: "/media/reel/lovingcup-storygift.mp4",
    poster: "/media/reel/lovingcup-storygift.jpg",
    handle: "@lovingcupwine",
    caption:
      "Vertical Instagram Story cut for Loving Cup Vineyard, short, scroll-stopping gift-shop creative driving DM inquiries and visits.",
    aspect: "9/16",
  },
  {
    id: "mack-summer-signature",
    client: "Mack Morris Heating & Air · Summer Signature",
    industry: "HVAC",
    tradeSlug: "hvac",
    vertical: "home-services",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/mack-summer-sig.mp4",
    poster: "/media/reel/mack-summer-sig.jpg",
    caption:
      ":15 TV + social signature spot for Mack Morris Heating & Air, summer heat-season hook with strong brand recall and clear emergency-service CTA.",
    aspect: "16/9",
  },
  {
    id: "ahip-mirror-roof-replacement",
    client: "AHIP · Mirror Roof Replacement",
    industry: "Non-Profit",
    tradeSlug: "non-profit",
    vertical: "home-services",
    format: "reel",
    status: "aired",
    src: "/media/reel/ahip-roof.mp4",
    poster: "/media/reel/ahip-roof.jpg",
    handle: "@ahiproofing",
    caption:
      "Vertical social Reel for Albemarle Housing Improvement Program (AHIP), non-profit storytelling showcasing a free roof-replacement made possible by community partners. Drives donations and volunteer interest.",
    aspect: "9/16",
  },
  {
    id: "ahip-50-years",
    client: "AHIP · 50 Years",
    industry: "Non-Profit",
    tradeSlug: "non-profit",
    vertical: "home-services",
    format: "reel",
    status: "aired",
    src: "/media/reel/ahip-50years.mp4",
    poster: "/media/reel/ahip-50years.jpg",
    handle: "@ahipcville",
    caption:
      ":05 vertical social Reel for AHIP, celebrating 50 years of home-repair impact across the Charlottesville community. Brand-affinity creative driving donations and supporter sign-ups.",
    aspect: "9/16",
  },
  {
    id: "ahip-give-today",
    client: "AHIP · Give Today",
    industry: "Non-Profit",
    tradeSlug: "non-profit",
    vertical: "home-services",
    format: "reel",
    status: "aired",
    src: "/media/reel/ahip-givetoday.mp4",
    poster: "/media/reel/ahip-givetoday.jpg",
    handle: "@ahipcville",
    caption:
      ":05 vertical social Reel for AHIP, direct-response \"Give Today\" creative driving year-end giving and recurring donor sign-ups.",
    aspect: "9/16",
  },
  {
    id: "floydfest-jj-grey",
    client: "FloydFest · JJ Grey & MoFro",
    industry: "Event",
    tradeSlug: "events-festivals",
    vertical: "tourism",
    format: "reel",
    status: "aired",
    src: "/media/reel/floydfest-jjgrey.mp4",
    poster: "/media/reel/floydfest-jjgrey.jpg",
    handle: "@floydfest",
    caption:
      ":13 vertical social Reel for FloydFest, JJ Grey & MoFro headliner-spotlight creative driving festival ticket sales and weekend visits.",
    aspect: "9/16",
  },
  {
    id: "wayne-theatre-elf",
    client: "Wayne Theatre · Elf The Musical",
    industry: "Event",
    tradeSlug: "events-festivals",
    vertical: "tourism",
    format: "reel",
    status: "aired",
    src: "/media/reel/wayne-elf-reel.mp4",
    poster: "/media/reel/wayne-elf-reel.jpg",
    handle: "@waynetheatre",
    caption:
      "Vertical social cut for Wayne Theatre (Waynesboro, VA), \"Elf The Musical\" holiday event creative driving ticket sales for the Nov 30 – Dec 10 run.",
    aspect: "9/16",
  },
  {
    id: "blue-toad-into-orchard",
    client: "Blue Toad Hard Cider · Into The Orchard",
    industry: "Winery / Brewery / Cidery / Venue",
    tradeSlug: "wineries-breweries",
    vertical: "tourism",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/blue-toad-into-orchard.mp4",
    poster: "/media/reel/blue-toad-into-orchard.jpg",
    caption:
      "TV + social commercial for Blue Toad Hard Cider, \"Into The Orchard\" creative spotlighting Virginia's craft-cidery story and driving direct-to-consumer case sales.",
    aspect: "16/9",
  },
  {
    id: "blue-toad-winter-2021",
    client: "Blue Toad Hard Cider · Winter 2021",
    industry: "Winery / Brewery / Cidery / Venue",
    tradeSlug: "wineries-breweries",
    vertical: "tourism",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/blue-toad-winter-2021.mp4",
    poster: "/media/reel/blue-toad-winter-2021.jpg",
    caption:
      "TV + social commercial for Blue Toad Hard Cider, Winter 2021 seasonal cut driving cold-weather case sales and tasting-room visits.",
    aspect: "16/9",
  },
  {
    id: "corn-maze-event",
    client: "Corn Maze · 1-Minute Event",
    industry: "Event",
    tradeSlug: "events-festivals",
    vertical: "tourism",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/corn-maze.mp4",
    poster: "/media/reel/corn-maze.jpg",
    caption:
      ":60 TV + social event commercial for Corn Maze, fall agritourism creative driving family-visit ticket sales and weekend traffic.",
    aspect: "16/9",
  },
  {
    id: "music-in-the-mountains",
    client: "Music in the Mountains",
    industry: "Event",
    tradeSlug: "events-festivals",
    vertical: "tourism",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/music-in-the-mountains.mp4",
    poster: "/media/reel/music-in-the-mountains.jpg",
    caption:
      ":18 TV + social event commercial for Music in the Mountains, live-music festival creative driving ticket sales and out-of-market visits.",
    aspect: "16/9",
  },
  {
    id: "love-ride-feb24",
    client: "Love Ridge · February '24",
    industry: "Event",
    tradeSlug: "events-festivals",
    vertical: "tourism",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/loveride-feb24.mp4",
    poster: "/media/reel/loveride-feb24.jpg",
    caption:
      ":44 TV + social event commercial for Love Ridge, February '24 cut driving winter-season event registrations and resort visits.",
    aspect: "16/9",
  },
  {
    id: "love-ridge-apr24",
    client: "Love Ridge · April '24",
    industry: "Event",
    tradeSlug: "events-festivals",
    vertical: "tourism",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/loveridge-apr24.mp4",
    poster: "/media/reel/loveridge-apr24.jpg",
    caption:
      ":42 TV + social event commercial for Love Ridge, April '24 cut driving spring-season event registrations and resort visits.",
    aspect: "16/9",
  },
  {
    id: "peak-homes-brand",
    client: "Peak Builders · Brand",
    industry: "Real Estate / Developer / Home Builder",
    tradeSlug: "real-estate",
    vertical: "home-services",
    format: "reel",
    status: "aired",
    src: "/media/reel/peak-homes.mp4",
    poster: "/media/reel/peak-homes.jpg",
    handle: "@peakbuildersva",
    caption:
      ":13 vertical Reel for Peak Builders, brand-awareness cut spotlighting custom-home craftsmanship and process.",
    aspect: "9/16",
  },
  {
    id: "narmada-winery-static",
    client: "Narmada Winery · Award Winning Wines",
    industry: "Winery / Brewery / Cidery / Venue",
    tradeSlug: "wineries-breweries",
    vertical: "tourism",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/narmada-square.mp4",
    poster: "/media/reel/narmada.jpg",
    caption:
      "Square static social creative for Narmada Winery, \"Award Winning Wines in the Foothills of the Blue Ridge Mountains\" with ORDER TODAY CTA driving direct-to-consumer wine sales.",
    aspect: "1/1",
  },
  {
    id: "crc-february-reel",
    client: "Charlottesville Remodeling Company · February Reel",
    industry: "Home Remodeling and Building Supply",
    tradeSlug: "home-remodeling-building-supply",
    vertical: "home-services",
    format: "reel",
    status: "aired",
    src: "/media/reel/crc-feb-reel.mp4",
    poster: "/media/reel/crc-feb-reel.jpg",
    handle: "@crcremodeling",
    caption:
      "Vertical Reel for Charlottesville Remodeling Company, owner-led, story-driven creative driving qualified consultation requests for kitchen, bath, and whole-home remodels.",
    aspect: "9/16",
  },
  {
    id: "visit-nelson-love-letter",
    client: "Visit Nelson County · Love Letter",
    industry: "Tourism DMO",
    tradeSlug: "dmo",
    vertical: "tourism",
    format: "reel",
    status: "aired",
    src: "/media/reel/love-letter-nelson.mp4",
    poster: "/media/reel/love-letter-nelson.jpg",
    handle: "@visitnelsoncounty",
    caption:
      "Long-form social love letter to Nelson County, destination-marketing storytelling driving overnight stays and trail visits.",
    aspect: "9/16",
  },
  {
    id: "visit-nelson-spoke-hub-b",
    client: "Visit Nelson County · Spoke Hub B",
    industry: "Tourism DMO",
    tradeSlug: "dmo",
    vertical: "tourism",
    format: "reel",
    status: "aired",
    src: "/media/reel/nelson-spoke-b.mp4",
    poster: "/media/reel/nelson-spoke-b.jpg",
    handle: "@visitnelsoncounty",
    caption:
      ":14 vertical Reel for Visit Nelson County, second hub-and-spoke cut driving trail-discovery and lodging inquiries.",
    aspect: "9/16",
  },
  {
    id: "rts-roofing-winter",
    client: "RTS Roofing · Winter",
    industry: "Roofing",
    tradeSlug: "roofing",
    vertical: "home-services",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/rts-winter.mp4",
    poster: "/media/reel/rts-winter.jpg",
    caption:
      ":30 TV + social commercial for RTS Roofing, winter cut emphasizing ice/snow damage prevention with a strong local-trust hook and clear booking CTA.",
    aspect: "16/9",
  },
  {
    id: "buildsage-30sec",
    client: "BuildSage · :30 Brand",
    industry: "Real Estate / Developer / Home Builder",
    tradeSlug: "real-estate",
    vertical: "home-services",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/buildsage.mp4",
    poster: "/media/reel/buildsage.jpg",
    caption:
      ":30 brand commercial for BuildSage, TV + social cut positioning the platform with clear value-prop messaging for the building-trades audience.",
    aspect: "16/9",
  },
  {
    id: "gotcha-covered-allustra",
    client: "Gotcha Covered · Allustra",
    industry: "Home Remodeling and Building Supply",
    tradeSlug: "home-remodeling-building-supply",
    vertical: "home-services",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/gotcha-allustra.mp4",
    poster: "/media/reel/gotcha-allustra.jpg",
    caption:
      ":30 TV + social commercial for Gotcha Covered featuring the Allustra window-treatment line, premium product-storytelling driving consultation bookings.",
    aspect: "16/9",
  },
  {
    id: "craig-builders",
    client: "Craig Builders",
    industry: "Real Estate / Developer / Home Builder",
    tradeSlug: "real-estate",
    vertical: "home-services",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/craig-builders.mp4",
    poster: "/media/reel/craig-builders.jpg",
    caption:
      ":39 TV + social commercial for Craig Builders, Charlottesville-area home builder, lifestyle-led creative driving new-construction inquiries.",
    aspect: "16/9",
  },
  {
    id: "dch-general-2025",
    client: "Dominion Custom Homes · General 2025",
    industry: "Real Estate / Developer / Home Builder",
    tradeSlug: "real-estate",
    vertical: "home-services",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/dch-general-2025.mp4",
    poster: "/media/reel/dch-general-2025.jpg",
    caption:
      ":30 TV + social brand commercial for Dominion Custom Homes, 2025 general-awareness creative driving new-build consultation requests.",
    aspect: "16/9",
  },
  {
    id: "dch-concept-completion-horz",
    client: "Dominion Custom Homes · Concept to Completion",
    industry: "Real Estate / Developer / Home Builder",
    tradeSlug: "real-estate",
    vertical: "home-services",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/dch-concept-completion-horz.mp4",
    poster: "/media/reel/dch-concept-completion-horz.jpg",
    caption:
      ":50 long-form TV commercial for Dominion Custom Homes, full \"Concept to Completion\" process story driving high-intent new-build leads.",
    aspect: "16/9",
  },
  {
    id: "dch-concept-completion-vert",
    client: "Dominion Custom Homes · Concept to Completion · Reel",
    industry: "Real Estate / Developer / Home Builder",
    tradeSlug: "real-estate",
    vertical: "home-services",
    format: "reel",
    status: "aired",
    src: "/media/reel/dch-concept-completion-vert.mp4",
    poster: "/media/reel/dch-concept-completion-vert.jpg",
    handle: "@dominioncustomhomes",
    caption:
      ":25 vertical Reel for Dominion Custom Homes, short-form \"Concept to Completion\" cut driving Instagram-and-TikTok new-build inquiries.",
    aspect: "9/16",
  },
  {
    id: "dch-graphics-vert",
    client: "Dominion Custom Homes · Box Graphics",
    industry: "Real Estate / Developer / Home Builder",
    tradeSlug: "real-estate",
    vertical: "home-services",
    format: "reel",
    status: "aired",
    src: "/media/reel/dch-graphics-vert.mp4",
    poster: "/media/reel/dch-graphics-vert.jpg",
    handle: "@dominioncustomhomes",
    caption:
      ":36 vertical Reel for Dominion Custom Homes, animated box-graphics creative spotlighting builds, process, and brand positioning.",
    aspect: "9/16",
  },
  {
    id: "bramante-homes-2",
    client: "Bramante Homes · Brand Reel A",
    industry: "Real Estate / Developer / Home Builder",
    tradeSlug: "real-estate",
    vertical: "home-services",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/bramante-homes-2.mp4",
    poster: "/media/reel/bramante-homes-2.jpg",
    caption:
      ":30 TV + social brand commercial for Bramante Homes, Charlottesville-area custom builder, lifestyle-led creative driving new-construction inquiries.",
    aspect: "16/9",
  },
  {
    id: "bramante-homes-30",
    client: "Bramante Homes · Brand Reel B",
    industry: "Real Estate / Developer / Home Builder",
    tradeSlug: "real-estate",
    vertical: "home-services",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/bramante-homes-30.mp4",
    poster: "/media/reel/bramante-homes-30.jpg",
    caption:
      ":30 TV + social brand commercial for Bramante Homes, second cut spotlighting craftsmanship, finishes, and new-home walkthroughs.",
    aspect: "16/9",
  },
  {
    id: "better-living-andersen-windows-mirror",
    client: "Better Living · Andersen Windows · Social Mirror",
    industry: "Home Remodeling and Building Supply",
    tradeSlug: "home-remodeling-building-supply",
    vertical: "home-services",
    format: "reel",
    status: "aired",
    src: "/media/reel/bl-andersen-windows-mirror.mp4",
    poster: "/media/reel/bl-andersen-windows-mirror.jpg",
    handle: "@betterlivingbs",
    caption:
      ":13 vertical Social Mirror Reel for Better Living Building Supply, Andersen Windows feature creative built for short-form social feeds and high engagement.",
    aspect: "9/16",
  },
  {
    id: "better-living-andersen-doors-mirror",
    client: "Better Living · Andersen Doors · Social Mirror",
    industry: "Home Remodeling and Building Supply",
    tradeSlug: "home-remodeling-building-supply",
    vertical: "home-services",
    format: "reel",
    status: "aired",
    src: "/media/reel/bl-andersen-doors-mirror.mp4",
    poster: "/media/reel/bl-andersen-doors-mirror.jpg",
    handle: "@betterlivingbs",
    caption:
      ":11 vertical Social Mirror Reel for Better Living Building Supply, Andersen Doors feature creative driving showroom visits and consultation requests.",
    aspect: "9/16",
  },
  {
    id: "better-living-trex-mirror-april",
    client: "Better Living · Trex Decking · Social Mirror April",
    industry: "Home Remodeling and Building Supply",
    tradeSlug: "home-remodeling-building-supply",
    vertical: "home-services",
    format: "reel",
    status: "aired",
    src: "/media/reel/bl-trex-mirror-april.mp4",
    poster: "/media/reel/bl-trex-mirror-april.jpg",
    handle: "@betterlivingbs",
    caption:
      ":09 vertical Social Mirror Reel for Better Living Building Supply, April Trex composite-decking cut built for short-form spring-season feeds.",
    aspect: "9/16",
  },
  {
    id: "better-living-trex-spring24",
    client: "Better Living · Trex Decking · Spring '24",
    industry: "Home Remodeling and Building Supply",
    tradeSlug: "home-remodeling-building-supply",
    vertical: "home-services",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/bl-trex-spring24.mp4",
    poster: "/media/reel/bl-trex-spring24.jpg",
    caption:
      ":24 square social commercial for Better Living Building Supply, Trex Decking Spring '24 cut driving showroom visits and design consultations.",
    aspect: "1/1",
  },
  {
    id: "better-living-cabinets-feb",
    client: "Better Living Building Supply · Cabinets · February Social",
    industry: "Home Remodeling and Building Supply",
    tradeSlug: "home-remodeling-building-supply",
    vertical: "home-services",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/ccw-spicy-cat-sangria.mp4",
    poster: "/media/reel/ccw-spicy-cat-sangria.jpg",
    caption:
      ":27 square social commercial for Better Living Building Supply, February cabinets feature driving kitchen-remodel consultations and showroom visits.",
    aspect: "1/1",
  },
  {
    id: "better-living-andersen-windows",
    client: "Better Living · Andersen Windows",
    industry: "Home Remodeling and Building Supply",
    tradeSlug: "home-remodeling-building-supply",
    vertical: "home-services",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/bl-andersen.mp4",
    poster: "/media/reel/bl-andersen.jpg",
    caption:
      "Social commercial for Better Living Building Supply, Andersen Windows feature creative built for high-engagement 1200×628 placements.",
    aspect: "16/9",
  },
  {
    id: "better-living-dollhouse",
    client: "Better Living · Dollhouse Theme",
    industry: "Home Remodeling and Building Supply",
    tradeSlug: "home-remodeling-building-supply",
    vertical: "home-services",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/bl-dollhouse.mp4",
    poster: "/media/reel/bl-dollhouse.jpg",
    caption:
      ":30 TV + web commercial for Better Living Building Supply, Dollhouse-themed showroom creative spotlighting design inspiration for the home.",
    aspect: "16/9",
  },
  {
    id: "better-living-muse",
    client: "Better Living · Muse Theme",
    industry: "Home Remodeling and Building Supply",
    tradeSlug: "home-remodeling-building-supply",
    vertical: "home-services",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/bl-muse.mp4",
    poster: "/media/reel/bl-muse.jpg",
    caption:
      ":35 TV + web commercial for Better Living Building Supply, Muse-themed showroom creative built to drive showroom visits and consultation requests.",
    aspect: "16/9",
  },
  {
    id: "rts-ahip-gives-back",
    client: "Roof Top Services · AHIP Gives Back",
    industry: "Roofing",
    tradeSlug: "roofing",
    vertical: "home-services",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/rts-ahip-givesback.mp4",
    poster: "/media/reel/rts-ahip-givesback.jpg",
    caption:
      ":30 TV + social commercial for Roof Top Services, AHIP Gives Back partnership creative, story-driven community-impact storytelling building brand affinity and local trust.",
    aspect: "16/9",
  },
  {
    id: "reico-kitchen-bath",
    client: "Reico Kitchen & Bath",
    industry: "Home Remodeling and Building Supply",
    tradeSlug: "home-remodeling-building-supply",
    vertical: "home-services",
    format: "reel",
    status: "aired",
    src: "/media/reel/reico.mp4",
    poster: "/media/reel/reico.jpg",
    handle: "@reicokitchenandbath",
    caption:
      ":13 vertical Reel for Reico Kitchen & Bath, premium remodel-showroom creative driving consultation requests and showroom visits.",
    aspect: "9/16",
  },
  {
    id: "gingerich-bge",
    client: "Gingerich · Big Green Egg",
    industry: "Retail",
    tradeSlug: "retail",
    vertical: "home-services",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/gingerich-bge.mp4",
    poster: "/media/reel/gingerich-bge.jpg",
    caption:
      ":30 TV + social commercial for Gingerich, Big Green Egg featured spot driving in-store demos and seasonal grill sales.",
    aspect: "16/9",
  },
  {
    id: "gingerich-grillmaster",
    client: "Gingerich · Grillmaster at Work",
    industry: "Retail",
    tradeSlug: "retail",
    vertical: "home-services",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/gingerich-grillmaster.mp4",
    poster: "/media/reel/gingerich-grillmaster.jpg",
    caption:
      ":43 square social commercial for Gingerich, grillmaster-at-work creative spotlighting Big Green Egg cooking and driving in-store visits.",
    aspect: "1/1",
  },
  {
    id: "piedmont-blues-festival",
    client: "Piedmont Blues Festival",
    industry: "Event",
    tradeSlug: "events-festivals",
    vertical: "tourism",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/piedmont-blues.mp4",
    poster: "/media/reel/piedmont-blues.jpg",
    caption:
      ":30 TV + social commercial for Piedmont Blues Festival, event-marketing creative driving ticket sales and weekend visitors.",
    aspect: "16/9",
  },
  {
    id: "atlantic-elevators-1",
    client: "Atlantic Elevators · Reel A",
    industry: "Misc Home Services",
    tradeSlug: "elevators",
    vertical: "home-services",
    format: "reel",
    status: "aired",
    src: "/media/reel/atlantic-elevators-1.mp4",
    poster: "/media/reel/atlantic-elevators-1.jpg",
    handle: "@atlanticelevators",
    caption:
      ":05 vertical social Reel for Atlantic Elevators, residential-accessibility creative driving consultation requests and in-home estimates.",
    aspect: "9/16",
  },
  {
    id: "atlantic-elevators-2",
    client: "Atlantic Elevators · Reel B",
    industry: "Misc Home Services",
    tradeSlug: "elevators",
    vertical: "home-services",
    format: "reel",
    status: "aired",
    src: "/media/reel/atlantic-elevators-2.mp4",
    poster: "/media/reel/atlantic-elevators-2.jpg",
    handle: "@atlanticelevators",
    caption:
      ":05 vertical social Reel for Atlantic Elevators, second-cut creative spotlighting home elevators and stair-lift installs.",
    aspect: "9/16",
  },
  {
    id: "better-living-trex-puppy",
    client: "Better Living · Trex Decking · Puppy",
    industry: "Home Remodeling and Building Supply",
    tradeSlug: "home-remodeling-building-supply",
    vertical: "home-services",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/trex-puppy.mp4",
    poster: "/media/reel/trex-puppy.jpg",
    caption:
      ":15 TV + social commercial for Better Living Building Supply featuring Trex composite decking, \"Puppy\" lifestyle creative driving showroom visits and design consultations.",
    aspect: "16/9",
  },
  {
    id: "better-living-trex-wedding",
    client: "Better Living · Trex Decking · Wedding",
    industry: "Home Remodeling and Building Supply",
    tradeSlug: "home-remodeling-building-supply",
    vertical: "home-services",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/trex-wedding.mp4",
    poster: "/media/reel/trex-wedding.jpg",
    caption:
      ":15 TV + social commercial for Better Living Building Supply featuring Trex composite decking, \"Wedding\" lifestyle creative spotlighting entertaining-ready outdoor living.",
    aspect: "16/9",
  },
  {
    id: "better-living-trex-princess",
    client: "Better Living · Trex Decking · Princess",
    industry: "Home Remodeling and Building Supply",
    tradeSlug: "home-remodeling-building-supply",
    vertical: "home-services",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/trex-princess.mp4",
    poster: "/media/reel/trex-princess.jpg",
    caption:
      ":15 TV + social commercial for Better Living Building Supply featuring Trex composite decking, \"Princess\" lifestyle creative driving project inquiries and design appointments.",
    aspect: "16/9",
  },
  {
    id: "better-living-trex-fireflies",
    client: "Better Living · Trex Decking · Fireflies",
    industry: "Home Remodeling and Building Supply",
    tradeSlug: "home-remodeling-building-supply",
    vertical: "home-services",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/trex-fireflies.mp4",
    poster: "/media/reel/trex-fireflies.jpg",
    caption:
      ":15 TV + social commercial for Better Living Building Supply featuring Trex composite decking, \"Fireflies\" lifestyle creative built for evening-time emotional resonance.",
    aspect: "16/9",
  },
  {
    id: "eat-drink-love-trail",
    client: "Eat. Drink. Love. · Gamified Tourism Trail",
    industry: "Tourism DMO",
    tradeSlug: "dmo",
    vertical: "tourism",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/eat-drink-love.mp4",
    poster: "/media/reel/eat-drink-love.jpg",
    handle: "@eatdrinkloveva",
    caption:
      ":12 square Instagram post for Eat. Drink. Love., gamified tourism-trail creative driving visitor sign-ups, on-trail check-ins, and partner-business visits across the region.",
    aspect: "1/1",
  },
  {
    id: "edl-checkin-and-win",
    client: "Eat. Drink. Love. · Check-in & Win",
    industry: "Tourism DMO",
    tradeSlug: "dmo",
    vertical: "tourism",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/edl-checkin-and-win.mp4",
    poster: "/media/reel/edl-checkin-and-win.jpg",
    handle: "@eatdrinkloveva",
    caption:
      ":32 TV + social commercial for Eat. Drink. Love., \"Check-in & Win\" creative driving trail sign-ups and partner-business visits via gamified rewards.",
    aspect: "16/9",
  },
  {
    id: "edl-farm-fb",
    client: "Eat. Drink. Love. · Farm Tours Facebook",
    industry: "Tourism DMO",
    tradeSlug: "dmo",
    vertical: "tourism",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/edl-farm-fb.mp4",
    poster: "/media/reel/edl-farm-fb.jpg",
    handle: "@eatdrinkloveva",
    caption:
      ":59 Facebook commercial for Eat. Drink. Love., Farm Tours feature driving agritourism visits and trail check-ins across partner farms.",
    aspect: "16/9",
  },
  {
    id: "edl-farm-newsfeed",
    client: "Eat. Drink. Love. · Farm Tours Newsfeed",
    industry: "Tourism DMO",
    tradeSlug: "dmo",
    vertical: "tourism",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/edl-farm-newsfeed.mp4",
    poster: "/media/reel/edl-farm-newsfeed.jpg",
    handle: "@eatdrinkloveva",
    caption:
      ":13 short-form newsfeed commercial for Eat. Drink. Love., Farm Tours teaser cut built for scroll-stopping in-feed engagement.",
    aspect: "16/9",
  },
  {
    id: "edl-winter",
    client: "Eat. Drink. Love. · Winter",
    industry: "Tourism DMO",
    tradeSlug: "dmo",
    vertical: "tourism",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/edl-winter.mp4",
    poster: "/media/reel/edl-winter.jpg",
    handle: "@eatdrinkloveva",
    caption:
      ":37 TV + social commercial for Eat. Drink. Love., winter-season cut driving off-peak trail sign-ups and partner-business visits.",
    aspect: "16/9",
  },
  {
    id: "nelson-dark-skies",
    client: "Visit Nelson County · Dark Skies",
    industry: "Tourism DMO",
    tradeSlug: "dmo",
    vertical: "tourism",
    format: "reel",
    status: "aired",
    src: "/media/reel/nelson-dark-skies.mp4",
    poster: "/media/reel/nelson-dark-skies.jpg",
    handle: "@visitnelsoncounty",
    caption:
      ":13 vertical Stories cut for Visit Nelson County, Dark Skies astronomy-tourism creative driving off-peak overnight stays and stargazing visits.",
    aspect: "9/16",
  },
  {
    id: "nelson-invades-151",
    client: "Visit Nelson County · Nelson Invades 151",
    industry: "Tourism DMO",
    tradeSlug: "dmo",
    vertical: "tourism",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/nelson-invades-151.mp4",
    poster: "/media/reel/nelson-invades-151.jpg",
    handle: "@visitnelsoncounty",
    caption:
      ":47 TV + social commercial for Visit Nelson County, \"Nelson 151 Invades Virginia Beach\" campaign creative driving cross-market awareness and trail visits.",
    aspect: "16/9",
  },
  {
    id: "nelson-wanderlove",
    client: "Visit Nelson County · WanderLove",
    industry: "Tourism DMO",
    tradeSlug: "dmo",
    vertical: "tourism",
    format: "tv-social",
    status: "aired",
    src: "/media/reel/nelson-wanderlove.mp4",
    poster: "/media/reel/nelson-wanderlove.jpg",
    handle: "@visitnelsoncounty",
    caption:
      ":15 TV + social commercial for Visit Nelson County, WanderLove regional-Virginia campaign creative driving day-trip and weekend-getaway visits.",
    aspect: "16/9",
  },
  {
    id: "nelson-spring-1",
    client: "Visit Nelson County · Spring · A",
    industry: "Tourism DMO",
    tradeSlug: "dmo",
    vertical: "tourism",
    format: "reel",
    status: "aired",
    src: "/media/reel/nelson-spring-1.mp4",
    poster: "/media/reel/nelson-spring-1.jpg",
    handle: "@visitnelsoncounty",
    caption:
      ":09 vertical Reel for Visit Nelson County, spring-season creative driving warm-weather visits, blooms, and outdoor-adventure trail traffic.",
    aspect: "9/16",
  },
  {
    id: "nelson-spring-2",
    client: "Visit Nelson County · Spring · B",
    industry: "Tourism DMO",
    tradeSlug: "dmo",
    vertical: "tourism",
    format: "reel",
    status: "aired",
    src: "/media/reel/nelson-spring-2.mp4",
    poster: "/media/reel/nelson-spring-2.jpg",
    handle: "@visitnelsoncounty",
    caption:
      ":09 vertical Reel for Visit Nelson County, second spring-season cut driving overnight stays and trail check-ins.",
    aspect: "9/16",
  },
  {
    id: "wanderlove-snowboard-reel",
    client: "Visit Nelson County · WanderLove · Snowboard & Trees",
    industry: "Tourism DMO",
    tradeSlug: "dmo",
    vertical: "tourism",
    format: "reel",
    status: "aired",
    src: "/media/reel/wl-snowboard-300x600.mp4",
    poster: "/media/reel/wl-snowboard-300x600.jpg",
    handle: "@visitnelsoncounty",
    caption:
      ":09 vertical WanderLove cut, snowboard-and-trees winter creative driving off-season ski-resort and trail visits.",
    aspect: "9/16",
  },
  {
    id: "wanderlove-winter-friends-reel",
    client: "Visit Nelson County · WanderLove · Winter Friends",
    industry: "Tourism DMO",
    tradeSlug: "dmo",
    vertical: "tourism",
    format: "reel",
    status: "aired",
    src: "/media/reel/wl-winter-friends-300x600.mp4",
    poster: "/media/reel/wl-winter-friends-300x600.jpg",
    handle: "@visitnelsoncounty",
    caption:
      ":09 vertical WanderLove cut, winter-friends lifestyle creative driving group-trip and lodging inquiries.",
    aspect: "9/16",
  },
];

// ─────────────────────────────────────────────────────────────
// DISPLAY CAMPAIGNS — one entry per client. Each "slot" is an
// ARRAY so a client can have multiple flights per size (e.g.
// April + July creative for the same 320×50 placement).
//
// Slot keys (drop into matching arrays):
//   banner728     → 728×90  Tablet Leaderboard
//   banner320     → 320×50  Mobile Phone Banner
//   mrec          → 300×250 Medium Rectangle (MREC)
//   interstitial  → 320×480 Mobile Interstitial
// ─────────────────────────────────────────────────────────────
export const displayCampaigns = [
  {
    id: "wanderlove",
    client: "Visit Nelson County · WanderLove",
    industry: "Tourism DMO",
    tradeSlug: "dmos",
    vertical: "tourism",
    status: "aired",
    caption:
      "Multi-flight mobile + tablet display campaign across IAB inventory. Same brand, refreshed seasonal art direction.",
    slots: {
      banner728: [
        {
          src: "/media/reel/wanderlove-darkskies-728x90.gif",
          label: "Dark Skies",
        },
      ],
      banner320: [
        { src: "/media/reel/wanderlove-apr-320x50.gif", label: "April" },
        { src: "/media/reel/wanderlove-jul-320x50.gif", label: "July" },
      ],
      mrec: [
        { src: "/media/reel/wanderlove-apr-300x250.gif", label: "April" },
        { src: "/media/reel/wanderlove-jul-300x250.gif", label: "July" },
      ],
      interstitial: [
        { src: "/media/reel/wanderlove-jul-320x480.gif", label: "July" },
      ],
    },
  },
  {
    id: "sticks-kebob-shop",
    client: "Sticks Kebob Shop",
    industry: "Restaurant / Food & Beverage",
    tradeSlug: "restaurants",
    vertical: "tourism",
    status: "aired",
    caption:
      "Mobile + tablet display set for a Charlottesville fast-casual favorite. Bold brand-forward creative built to drive in-store visits and online orders.",
    slots: {
      banner728: [
        { src: "/media/reel/sticks-jun-728x90.gif", label: "June" },
      ],
      banner320: [
        { src: "/media/reel/sticks-jun-320x50.gif", label: "June" },
      ],
      mrec: [
        { src: "/media/reel/sticks-jun-300x250.gif", label: "June" },
      ],
      interstitial: [
        { src: "/media/reel/sticks-jun-320x480.gif", label: "June" },
      ],
    },
  },
  {
    id: "valley-road-vineyards",
    client: "Valley Road Vineyards",
    industry: "Winery / Brewery / Cidery / Venue",
    tradeSlug: "wineries-breweries",
    vertical: "tourism",
    status: "aired",
    caption:
      "Mobile + tablet display set for a Central Virginia winery just minutes from Crozet. Lifestyle-led creative driving tasting-room visits and event bookings.",
    slots: {
      banner728: [
        { src: "/media/reel/valley-roads-728x90.gif", label: "Minutes from Crozet" },
      ],
      banner320: [
        { src: "/media/reel/valley-roads-320x50.gif", label: "Minutes from Crozet" },
      ],
      mrec: [
        { src: "/media/reel/valley-roads-300x250.gif", label: "Minutes from Crozet" },
      ],
      interstitial: [
        { src: "/media/reel/valley-roads-320x480.gif", label: "Minutes from Crozet" },
      ],
    },
  },
  {
    id: "fifty-third-winery",
    client: "Fifty-Third Winery & Vineyard",
    industry: "Winery / Brewery / Cidery / Venue",
    tradeSlug: "wineries-breweries",
    vertical: "tourism",
    status: "aired",
    caption:
      "Mobile + tablet display set for a Louisa County, VA estate winery. Bottle-forward creative built to drive tasting-room visits, event RSVPs, and Chardonel sales.",
    slots: {
      banner728: [
        { src: "/media/reel/fifty-third-728x90.gif", label: "Events" },
      ],
      banner320: [
        { src: "/media/reel/fifty-third-320x50.gif", label: "Events" },
      ],
      mrec: [
        { src: "/media/reel/fifty-third-300x250.gif", label: "Events" },
      ],
      interstitial: [
        { src: "/media/reel/fifty-third-320x480.gif", label: "Events" },
      ],
    },
  },
  {
    id: "airflow-heating-air",
    client: "Airflow Heating and Air",
    industry: "HVAC",
    tradeSlug: "hvac",
    vertical: "home-services",
    status: "aired",
    caption:
      "Mobile + tablet display set for a 30-year HVAC contractor. Multi-flight campaign covering new-home installs and seasonal tune-ups, anchored by the Airflow + Trane brand lockup.",
    slots: {
      banner728: [
        { src: "/media/reel/airflow-728x90.gif", label: "New Home" },
        { src: "/media/reel/airflow-tuneup-728x90.gif", label: "Summer Tune Up" },
      ],
      banner320: [
        { src: "/media/reel/airflow-320x50.gif", label: "New Home" },
        { src: "/media/reel/airflow-tuneup-320x50.gif", label: "Summer Tune Up" },
      ],
      mrec: [
        { src: "/media/reel/airflow-300x250.gif", label: "New Home" },
        { src: "/media/reel/airflow-tuneup-300x250.gif", label: "Summer Tune Up" },
      ],
      interstitial: [
        { src: "/media/reel/airflow-320x480.gif", label: "New Home" },
        { src: "/media/reel/airflow-tuneup-320x480.gif", label: "Summer Tune Up" },
      ],
    },
  },
  {
    id: "gingerich-outdoor-power",
    client: "Gingerich · Outdoor Power Specialist",
    industry: "Landscaping / Lawn & Garden",
    tradeSlug: "lawn-landscape",
    vertical: "home-services",
    status: "aired",
    caption:
      "Mobile + tablet display set for a Toro dealer & outdoor power equipment specialist. December winterize promotion driving off-season service appointments with free pickup & delivery.",
    slots: {
      banner728: [
        { src: "/media/reel/gingerich-728x90.gif", label: "Winterize Special" },
      ],
      banner320: [
        { src: "/media/reel/gingerich-320x50.gif", label: "Winterize Special" },
      ],
      mrec: [
        { src: "/media/reel/gingerich-300x250.gif", label: "Winterize Special" },
      ],
      interstitial: [
        { src: "/media/reel/gingerich-320x480.gif", label: "Winterize Special" },
      ],
    },
  },
  {
    id: "loving-cup-vineyard",
    client: "Loving Cup Vineyard & Winery",
    industry: "Winery / Brewery / Cidery / Venue",
    tradeSlug: "wineries-breweries",
    vertical: "tourism",
    status: "aired",
    caption:
      "Mobile + tablet display set for a Central Virginia organic vineyard & winery. Minimalist brand-first creative driving tasting-room visits with a clear 'Plan Your Visit' CTA.",
    slots: {
      banner728: [
        { src: "/media/reel/loving-cup-728x90.gif", label: "Love Is Organic" },
      ],
      banner320: [
        { src: "/media/reel/loving-cup-320x50.gif", label: "Love Is Organic" },
      ],
      mrec: [
        { src: "/media/reel/loving-cup-300x250.gif", label: "Love Is Organic" },
      ],
      interstitial: [
        { src: "/media/reel/loving-cup-320x480.gif", label: "Love Is Organic" },
      ],
    },
  },
  {
    id: "pro-re-nata-brewery",
    client: "Pro Re Nata Brewery",
    industry: "Winery / Brewery / Cidery / Venue",
    tradeSlug: "wineries-breweries",
    vertical: "tourism",
    status: "aired",
    caption:
      "Mobile + tablet display set for a Crozet, VA brewery & live-music venue. Sunset patio imagery driving event RSVPs and event-rental inquiries.",
    slots: {
      banner728: [
        { src: "/media/reel/pro-re-nata-728x90.jpg", label: "October Events" },
      ],
      banner320: [
        { src: "/media/reel/pro-re-nata-320x50.jpg", label: "October Events" },
      ],
      mrec: [
        { src: "/media/reel/pro-re-nata-300x250.jpg", label: "October Events" },
      ],
      interstitial: [
        { src: "/media/reel/pro-re-nata-320x480.jpg", label: "October Events" },
      ],
    },
  },
  {
    id: "frys-spring-station",
    client: "Fry's Spring Station",
    industry: "Restaurant / Food & Beverage",
    tradeSlug: "restaurants",
    vertical: "tourism",
    status: "aired",
    caption:
      "Mobile + tablet display set for a beloved Charlottesville, VA restaurant. Hand-lettered brand voice and craveable food/milkshake hero shots driving menu views and visits.",
    slots: {
      banner728: [
        { src: "/media/reel/frys-728x90.gif", label: "Milkshakes" },
      ],
      banner320: [
        { src: "/media/reel/frys-320x50.gif", label: "Milkshakes" },
      ],
      mrec: [
        { src: "/media/reel/frys-300x250.gif", label: "Milkshakes" },
      ],
      interstitial: [
        { src: "/media/reel/frys-320x480.gif", label: "Milkshakes" },
      ],
    },
  },
  {
    id: "oliva",
    client: "Oliva · Oils & Balsamics",
    industry: "Retail",
    tradeSlug: "restaurants",
    vertical: "tourism",
    status: "aired",
    caption:
      "Multi-flight mobile + tablet display set for a Charlottesville specialty food retailer. Location-led and premium-product creative driving Barracks Road foot traffic and gourmet gift sales.",
    slots: {
      banner728: [
        { src: "/media/reel/oliva-728x90.gif", label: "Barracks Road" },
        { src: "/media/reel/oliva-premium-728x90.gif", label: "Premium Oils" },
      ],
      banner320: [
        { src: "/media/reel/oliva-320x50.gif", label: "Barracks Road" },
        { src: "/media/reel/oliva-premium-320x50.gif", label: "Premium Oils" },
      ],
      mrec: [
        { src: "/media/reel/oliva-300x250.gif", label: "Barracks Road" },
        { src: "/media/reel/oliva-premium-300x250.gif", label: "Premium Oils" },
      ],
      interstitial: [
        { src: "/media/reel/oliva-320x480.gif", label: "Barracks Road" },
        { src: "/media/reel/oliva-premium-320x480.gif", label: "Premium Oils" },
      ],
    },
  },
  {
    id: "crozet-winter-brews",
    client: "Crozet Winter Brews Festival",
    industry: "Event",
    tradeSlug: "events-festivals",
    vertical: "tourism",
    status: "aired",
    caption:
      "Mobile + tablet display set for a Central Virginia craft beer festival. Bold logo-forward creative + 'Virginia is for Craft Beer Lovers' lockup driving ticket sales for the December event at Crozet Park.",
    slots: {
      banner728: [
        { src: "/media/reel/crozet-brew-728x90.gif", label: "Buy Tix Now" },
      ],
      banner320: [
        { src: "/media/reel/crozet-brew-320x50.gif", label: "Buy Tix Now" },
      ],
      mrec: [
        { src: "/media/reel/crozet-brew-300x250.gif", label: "4 Beers" },
      ],
      interstitial: [
        { src: "/media/reel/crozet-brew-320x480.gif", label: "Buy Tix Now" },
      ],
    },
  },
  {
    id: "party-starts-here",
    client: "Party Starts Here · Charlottesville",
    industry: "Retail",
    tradeSlug: "retail",
    vertical: "tourism",
    status: "aired",
    caption:
      "Mobile + tablet display set for a Charlottesville party supplies retailer. Seasonal costume-flight creative driving in-store visits and online supply orders for kids' birthdays and themed parties.",
    slots: {
      banner728: [
        { src: "/media/reel/costumes-728x90.gif", label: "Costumes" },
      ],
      banner320: [
        { src: "/media/reel/costumes-320x50.gif", label: "Costumes" },
      ],
      mrec: [
        { src: "/media/reel/costumes-300x250.gif", label: "Costumes" },
      ],
      interstitial: [
        { src: "/media/reel/costumes-320x480.gif", label: "Costumes" },
      ],
    },
  },
  {
    id: "floydfest",
    client: "FloydFest 25 · Aurora",
    industry: "Event",
    tradeSlug: "events-festivals",
    vertical: "tourism",
    status: "aired",
    caption:
      "Mobile + tablet display set for the award-winning Music · Magic · Mountains festival. Aurora-themed creative driving 4-day ticket and VIP-experience sales for the July 23–27 event.",
    slots: {
      banner728: [
        { src: "/media/reel/floydfest-728x90.gif", label: "Aurora · Apr" },
      ],
      banner320: [
        { src: "/media/reel/floydfest-320x50.gif", label: "Aurora · Apr" },
      ],
      mrec: [
        { src: "/media/reel/floydfest-300x250.gif", label: "Aurora · Mar" },
      ],
      interstitial: [
        { src: "/media/reel/floydfest-320x480.gif", label: "Aurora · Apr" },
      ],
    },
  },
  {
    id: "dead-air",
    client: "Dead Air V · WNRN 102.5 FM Presents",
    industry: "Event",
    tradeSlug: "events-festivals",
    vertical: "tourism",
    status: "aired",
    caption:
      "Mobile + tablet display set promoting an annual Grateful Dead tribute show at The NorVa, presented by WNRN 102.5 FM. 20+ Commonwealth bands, 20+ Dead tunes, psychedelic skeleton-hand creative driving $30 all-in GA ticket sales.",
    slots: {
      banner728: [
        { src: "/media/reel/dead-air-728x90.gif", label: "Jan 25 · NorVa" },
      ],
      banner320: [
        { src: "/media/reel/dead-air-320x50.gif", label: "Jan 25 · NorVa" },
      ],
      mrec: [
        { src: "/media/reel/dead-air-300x250.gif", label: "Jan 25 · NorVa" },
      ],
      interstitial: [
        { src: "/media/reel/dead-air-320x480.gif", label: "Jan 25 · NorVa" },
      ],
    },
  },
  {
    id: "the-habitat-store",
    client: "The Habitat Store",
    industry: "Retail",
    tradeSlug: "retail",
    vertical: "tourism",
    status: "aired",
    caption:
      "Mobile + tablet display set for a Habitat for Humanity ReStore, used furniture & home goods. 'Welcome Students' back-to-school flight driving foot traffic from UVA students setting up off-campus apartments.",
    slots: {
      banner728: [
        { src: "/media/reel/habitat-728x90.gif", label: "Welcome Students" },
      ],
      banner320: [
        { src: "/media/reel/habitat-320x50.gif", label: "Welcome Students" },
      ],
      mrec: [
        { src: "/media/reel/habitat-300x250.gif", label: "Welcome Students" },
      ],
      interstitial: [
        { src: "/media/reel/habitat-320x480.gif", label: "Welcome Students" },
      ],
    },
  },
  {
    id: "cville-remodeling",
    client: "Charlottesville Remodeling Company",
    industry: "Home Remodeling and Building Supply",
    tradeSlug: "home-remodeling-building-supply",
    vertical: "home-services",
    status: "aired",
    caption:
      "Multi-flight mobile + tablet display set for a Central Virginia kitchen, bath & whole-home remodeler. Before/after split creative across kitchen and bathroom projects, anchored by 'Cville's #1 Improvement Contractor.'",
    slots: {
      banner728: [
        { src: "/media/reel/cville-remodeling-728x90.gif", label: "Kitchen" },
        { src: "/media/reel/cville-remodeling-bath-728x90.gif", label: "Bathroom" },
      ],
      banner320: [
        { src: "/media/reel/cville-remodeling-320x50.gif", label: "Kitchen" },
        { src: "/media/reel/cville-remodeling-bath-320x50.gif", label: "Bathroom" },
      ],
      mrec: [
        { src: "/media/reel/cville-remodeling-300x250.gif", label: "Kitchen" },
        { src: "/media/reel/cville-remodeling-bath-300x250.gif", label: "Bathroom" },
      ],
      interstitial: [
        { src: "/media/reel/cville-remodeling-320x480.gif", label: "Kitchen" },
        { src: "/media/reel/cville-remodeling-bath-320x480.gif", label: "Bathroom" },
      ],
    },
  },
  {
    id: "sticks-kebob-shop-autumn",
    client: "Sticks Kebob Shop · Autumn Bowls",
    industry: "Restaurant / Food & Beverage",
    tradeSlug: "restaurants",
    vertical: "tourism",
    status: "aired",
    caption:
      "Seasonal mobile + tablet display set for the Sticks Kebob Shop autumn bowls menu launch. Warm orange palette + bowl-hero creative driving menu views and in-store visits.",
    slots: {
      banner728: [
        { src: "/media/reel/sticks-autumn-728x90.gif", label: "Autumn Bowls" },
      ],
      banner320: [
        { src: "/media/reel/sticks-autumn-320x50.gif", label: "Autumn Bowls" },
      ],
      mrec: [
        { src: "/media/reel/sticks-autumn-300x250.gif", label: "Autumn Bowls" },
      ],
      interstitial: [
        { src: "/media/reel/sticks-autumn-320x480.gif", label: "Autumn Bowls" },
      ],
    },
  },
  {
    id: "parade-of-homes",
    client: "BRHB Parade of Homes · Nest Realty",
    industry: "Event",
    tradeSlug: "events-festivals",
    vertical: "tourism",
    status: "aired",
    caption:
      "Mobile + tablet display set for the Blue Ridge Home Builders Association Parade of Homes, annual hybrid showcase of new residential properties. Final-weekend urgency creative with Nest Realty co-brand driving in-person tours and virtual entries.",
    slots: {
      banner728: [
        { src: "/media/reel/final-wknd-728x90.gif", label: "Final Weekend" },
      ],
      banner320: [
        { src: "/media/reel/final-wknd-320x50.gif", label: "Final Weekend" },
      ],
      mrec: [
        { src: "/media/reel/final-wknd-300x250.gif", label: "Final Weekend" },
      ],
      interstitial: [
        { src: "/media/reel/final-wknd-320x480.gif", label: "Final Weekend" },
      ],
    },
  },
  {
    id: "mack-morris-display",
    client: "Mack Morris Heating & Air",
    industry: "HVAC",
    tradeSlug: "hvac",
    vertical: "home-services",
    status: "aired",
    caption:
      "Winter mobile + tablet display set for a regional HVAC contractor. Mascot duck + 'shivering homeowner' creative driving HVAC replacement leads in cold-snap moments.",
    slots: {
      banner728: [
        { src: "/media/reel/mack-morris-display-728x90.gif", label: "Replace Your HVAC" },
      ],
      banner320: [
        { src: "/media/reel/mack-morris-display-320x50.gif", label: "Replace Your HVAC" },
      ],
      mrec: [
        { src: "/media/reel/mack-morris-display-600x600.gif", label: "Replace Your HVAC" },
      ],
      interstitial: [
        { src: "/media/reel/mack-morris-display-320x480.gif", label: "Replace Your HVAC" },
      ],
    },
  },
  {
    id: "5th-street-station",
    client: "5th Street Station · Charlottesville",
    industry: "Retail",
    tradeSlug: "retail",
    vertical: "tourism",
    status: "aired",
    caption:
      "Mobile + tablet display set for a Charlottesville multi-tenant retail destination, Wegmans, A.C. Moore, Dick's Sporting Goods, Mailbox Express. Holiday-season co-op creative driving shopping-center foot traffic.",
    slots: {
      banner728: [
        { src: "/media/reel/5th-st-728x90.gif", label: "Holidays" },
      ],
      banner320: [
        { src: "/media/reel/5th-st-320x50.gif", label: "Holidays" },
      ],
      mrec: [
        { src: "/media/reel/5th-st-300x250.gif", label: "Holidays" },
      ],
      interstitial: [
        { src: "/media/reel/5th-st-320x480.gif", label: "Holidays" },
      ],
    },
  },
  {
    id: "octoaderfest",
    client: "Octoaderfest",
    industry: "Event",
    tradeSlug: "events-festivals",
    vertical: "tourism",
    status: "aired",
    caption:
      "Mobile + tablet display set for a fall Oktoberfest-style beer event. Bold blue-to-brand reveal creative driving ticket sales and day-of attendance.",
    slots: {
      banner728: [
        { src: "/media/reel/octoaderfest-728x90.gif", label: "Octoaderfest" },
      ],
      banner320: [
        { src: "/media/reel/octoaderfest-320x50.gif", label: "Octoaderfest" },
      ],
      mrec: [
        { src: "/media/reel/octoaderfest-300x250.gif", label: "Octoaderfest" },
      ],
      interstitial: [
        { src: "/media/reel/octoaderfest-320x480.gif", label: "Octoaderfest" },
      ],
    },
  },
  {
    id: "mack-morris-summer-heat",
    client: "Mack Morris Heating & Air · Summer Heat",
    industry: "HVAC",
    tradeSlug: "hvac",
    vertical: "home-services",
    status: "aired",
    caption:
      "Summer mobile display set for a regional HVAC contractor. Mascot duck + 'sweltering-sun' creative driving HVAC replacement leads during heat waves.",
    slots: {
      banner320: [
        { src: "/media/reel/mack-morris-heat-320x50.gif", label: "Heat · New Unit" },
      ],
      mrec: [
        { src: "/media/reel/mack-morris-heat-300x250.gif", label: "Heat · New Unit" },
      ],
      interstitial: [
        { src: "/media/reel/mack-morris-heat-320x480.gif", label: "Heat · New Unit" },
      ],
    },
  },
  {
    id: "rodeo-sept-3",
    client: "Rodeo · Sat Sept 3",
    industry: "Event",
    tradeSlug: "events-festivals",
    vertical: "tourism",
    status: "aired",
    caption:
      "Mobile + tablet display set for a Saturday September 3 rodeo event. Bold yellow brand-forward creative driving ticket sales and day-of attendance.",
    slots: {
      banner728: [
        { src: "/media/reel/rodeo-728x90.gif", label: "Sat Sept 3" },
      ],
      banner320: [
        { src: "/media/reel/rodeo-320x50.gif", label: "Sat Sept 3" },
      ],
      mrec: [
        { src: "/media/reel/rodeo-300x250.gif", label: "Sat Sept 3" },
      ],
    },
  },
  {
    id: "better-living-andersen",
    client: "Better Living · Andersen Windows & Doors",
    industry: "Home Remodeling and Building Supply",
    tradeSlug: "home-remodeling-building-supply",
    vertical: "home-services",
    status: "aired",
    caption:
      "Mobile display set for a Central Virginia building supply dealer featuring Andersen windows & doors. Multi-creative rotation across windows and doors flights driving showroom visits and contractor inquiries.",
    slots: {
      banner320: [
        { src: "/media/reel/better-living-windows-320x50.gif", label: "Windows" },
        { src: "/media/reel/better-living-doors-320x50.gif", label: "Doors" },
      ],
      mrec: [
        { src: "/media/reel/better-living-windows-300x250.gif", label: "Windows" },
      ],
    },
  },
  {
    id: "sticks-kebob-shop-catering",
    client: "Sticks Kebob Shop · Express Catering",
    industry: "Restaurant / Food & Beverage",
    tradeSlug: "restaurants",
    vertical: "tourism",
    status: "aired",
    caption:
      "Mobile + tablet display set for the Sticks Kebob Shop Express Catering line. Mother's Day, graduations & event-catering creative driving order inquiries and delivery requests.",
    slots: {
      banner728: [
        { src: "/media/reel/sticks-catering-728x90.gif", label: "Express Catering" },
      ],
      banner320: [
        { src: "/media/reel/sticks-catering-320x50.gif", label: "Express Catering" },
      ],
      mrec: [
        { src: "/media/reel/sticks-catering-300x250.gif", label: "Express Catering" },
      ],
      interstitial: [
        { src: "/media/reel/sticks-catering-320x480.gif", label: "Express Catering" },
      ],
    },
  },
  {
    id: "sticks-kebob-shop-rewards",
    client: "Sticks Kebob Shop · Rewards Tease",
    industry: "Restaurant / Food & Beverage",
    tradeSlug: "restaurants",
    vertical: "tourism",
    status: "aired",
    caption:
      "Teaser mobile + tablet display set for the Sticks Kebob Shop rewards program launch. Bold 'NEW' sunburst reveal creative driving sign-ups and app downloads.",
    slots: {
      banner728: [
        { src: "/media/reel/sticks-rewards-728x90.gif", label: "Rewards · NEW" },
      ],
      banner320: [
        { src: "/media/reel/sticks-rewards-320x50.gif", label: "Rewards · NEW" },
      ],
      mrec: [
        { src: "/media/reel/sticks-rewards-300x250.gif", label: "Rewards · NEW" },
      ],
      interstitial: [
        { src: "/media/reel/sticks-rewards-320x480.gif", label: "Rewards · NEW" },
      ],
    },
  },
  {
    id: "ben-and-jerrys-bike",
    client: "Ben & Jerry's · Bike",
    industry: "Restaurant / Food & Beverage",
    tradeSlug: "restaurants",
    vertical: "home-services",
    status: "aired",
    caption:
      "Mobile + tablet display set for Ben & Jerry's, bike-themed scoop-shop creative driving in-store visits and seasonal-flavor awareness.",
    slots: {
      mrec: [
        {
          src: "/media/reel/bjbike-300x250.mp4",
          poster: "/media/reel/bjbike-300x250.jpg",
          label: "Bike · 300×250",
        },
      ],
      halfpage: [
        {
          src: "/media/reel/bjbike-300x600.mp4",
          poster: "/media/reel/bjbike-300x600.jpg",
          label: "Bike · 300×600",
        },
      ],
    },
  },
  {
    id: "ccw-salty-bottom-blue-oysters",
    client: "Cunningham Creek Winery · Salty Bottom Blue Oysters · May 28",
    industries: ["Event", "Winery / Brewery / Cidery / Venue"],
    industry: "Event",
    tradeSlug: "events-festivals",
    vertical: "tourism",
    status: "aired",
    caption:
      "Multi-format display campaign for Cunningham Creek Winery × Salty Bottom Blue Oysters, May 28 live music + oysters event creative driving ticket sales and tasting-room visits.",
    slots: {
      banner728: [
        { src: "/media/reel/ccw-memorial-728x90.gif", label: "Leaderboard · 728×90" },
      ],
      banner320: [
        { src: "/media/reel/ccw-memorial-320x50.gif", label: "Mobile Leaderboard · 320×50" },
      ],
      mrec: [
        { src: "/media/reel/ccw-memorial-300x250.gif", label: "Medium Rectangle · 300×250" },
      ],
      interstitial: [
        { src: "/media/reel/ccw-memorial-320x480.gif", label: "Interstitial · 320×480" },
      ],
    },
  },
  {
    id: "ccw-spicy-cat-display",
    client: "Cunningham Creek Winery · Spicy Cat Sangria · Display Set",
    industry: "Winery / Brewery / Cidery / Venue",
    tradeSlug: "wineries-breweries",
    vertical: "tourism",
    status: "aired",
    caption:
      "Multi-format mobile + tablet display set for Cunningham Creek Winery, Spicy Cat Sangria \"Now Available\" creative driving direct-to-consumer sales and tasting-room visits.",
    slots: {
      banner728: [
        { src: "/media/reel/spicy-cat-728x90.gif", label: "Leaderboard · 728×90" },
      ],
      banner320: [
        { src: "/media/reel/spicy-cat-320x50.gif", label: "Mobile Leaderboard · 320×50" },
      ],
      mrec: [
        { src: "/media/reel/spicy-cat-300x250.gif", label: "Medium Rectangle · 300×250" },
      ],
      interstitial: [
        { src: "/media/reel/spicy-cat-320x480.gif", label: "Interstitial · 320×480" },
      ],
    },
  },
  {
    id: "blue-toad-into-orchard-display",
    client: "Blue Toad Hard Cider · Into The Orchard · Display Set",
    industry: "Winery / Brewery / Cidery / Venue",
    tradeSlug: "wineries-breweries",
    vertical: "tourism",
    status: "aired",
    caption:
      "Multi-format mobile + tablet display set for Blue Toad Hard Cider, \"Into The Orchard / Free Shipping on Full Case\" creative driving direct-to-consumer case purchases.",
    slots: {
      banner728: [
        { src: "/media/reel/bt-orchard-728x90.gif", label: "Leaderboard · 728×90" },
      ],
      banner320: [
        { src: "/media/reel/bt-orchard-320x50.gif", label: "Mobile Leaderboard · 320×50" },
      ],
      mrec: [
        { src: "/media/reel/bt-orchard-300x250.gif", label: "Medium Rectangle · 300×250" },
      ],
      interstitial: [
        { src: "/media/reel/bt-orchard-320x480.gif", label: "Interstitial · 320×480" },
      ],
    },
  },
];

// Combined export for the gallery — gives a single iterable.
export const sampleAds = [...videoAds, ...displayCampaigns];

// Helpers
export const videoAdsByTrade = (slug) =>
  videoAds.filter((a) => a.tradeSlug === slug);
export const displayCampaignsByTrade = (slug) =>
  displayCampaigns.filter((c) => c.tradeSlug === slug);

// Labels
export const FORMAT_LABEL = {
  "tv-social": "TV / Video Commercial",
  reel: "Reel · Vertical",
  "mobile-display": "Mobile Display Campaign",
  "social-static": "Facebook / Instagram",
  "social-carousel": "Facebook Carousel",
};

export const STATUS_LABEL = {
  aired: "Aired",
  spec: "Spec",
};

export const SLOT_META = {
  mrec: { name: "300×250", subtitle: "Medium Rectangle", aspect: "6/5" },
  interstitial: { name: "320×480", subtitle: "Mobile Interstitial", aspect: "2/3" },
  halfpage: { name: "300×600", subtitle: "Half Page", aspect: "1/2" },
  banner320: { name: "320×50", subtitle: "Phone Banner", aspect: "6.4/1" },
  banner728: { name: "728×90", subtitle: "Tablet Leaderboard", aspect: "8.09/1" },
};
